import {
  BaseModel,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import {
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { WatchablesFactory } from '../watchables/watchables-factory';

export type DocumentListItem = {
  id: string;
  ownerId: string;
  title: string | null;
  description: string | null;
  folderName: string | null;
  folderScope: 'user' | 'org' | 'external' | null;
  /** Effective visibility scope: folder scope combined with direct shares on the document. */
  scope: 'user' | 'org' | 'external' | null;
  updatedAt: Date;
};

export type ListParamsState = {
  page: number;
  limit: number;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
};

export const DEFAULT_LIST_PARAMS: ListParamsState = {
  page: 1,
  limit: 100,
  search: '',
  sort: undefined,
  sortDir: 'asc',
};

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_LIST_PARAMS.limit;
}

function parseListParams(params: URLSearchParams): ListParamsState {
  return {
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('search') ?? DEFAULT_LIST_PARAMS.search,
    sort: (() => {
      const v = params.get('sort');
      return v !== null && v !== '' ? v : undefined;
    })(),
    sortDir:
      params.get('sortDir') === 'desc' ? 'desc' : DEFAULT_LIST_PARAMS.sortDir,
  };
}

function serializeListParams(s: ListParamsState): Record<string, string> {
  return {
    page: s.page === DEFAULT_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    search: s.search,
    sort: s.sort ?? '',
    sortDir: s.sortDir === 'asc' ? '' : s.sortDir,
  };
}

function listParamsEquality(a: ListParamsState, b: ListParamsState): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.sort === b.sort &&
    a.sortDir === b.sortDir
  );
}

export type DocumentsListData = {
  items: DocumentListItem[];
  total: number;
};

export type DocumentsListPageModelProps = {
  orgId: string;
  queryClient: QueryClient;
  watchablesFactory: WatchablesFactory;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
  client: TenantedClient;
};

function documentsDataEquality(
  a: DocumentsListData | null,
  b: DocumentsListData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (
      a.items[i]?.id !== b.items[i]?.id ||
      a.items[i]?.title !== b.items[i]?.title ||
      a.items[i]?.description !== b.items[i]?.description ||
      a.items[i]?.folderName !== b.items[i]?.folderName ||
      a.items[i]?.folderScope !== b.items[i]?.folderScope ||
      a.items[i]?.scope !== b.items[i]?.scope ||
      a.items[i]?.updatedAt?.getTime() !== b.items[i]?.updatedAt?.getTime()
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Model for the documents list page. Owns URL-synced watchables for list params (page, limit,
 * search, sort, sortDir) and an event watchable that fetches document list data.
 */
export class DocumentsListPageModel extends BaseModel {
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<ListParamsState>;
  private readonly _listParams: ReadonlyWatchable<ListParamsState>;
  private readonly _documentsDataEventWatchable: ReadonlyEventWatchable<DocumentsListData | null>;

  constructor(props: DocumentsListPageModelProps) {
    super('documents-list-page');
    const initial = parseListParams(props.initialSearchParams);
    const adapter = props.urlAdapter;

    this._listParamsUrl = new MultiKeyUrlParamWatchable<ListParamsState>(
      ['page', 'limit', 'search', 'sort', 'sortDir'],
      {
        default: DEFAULT_LIST_PARAMS,
        parse: parseListParams,
        serialize: serializeListParams,
        equalityFn: listParamsEquality,
      },
      adapter,
      initial
    );

    this._listParams = this._listParamsUrl.asWatchable();

    const watchablesFactory = props.watchablesFactory;

    this._documentsDataEventWatchable =
      watchablesFactory.makeDocumentsListWatchable<
        DocumentsListData | null,
        { resourceType?: string }
      >({
        orgId: props.orgId,
        initialValue: null,
        equalityFn: documentsDataEquality,
        filter: (payload) => payload?.resourceType === 'document',
        fn: async (_payload, get) => {
          const { page, limit, search, sort, sortDir } = get(this._listParams);
          const allItems: DocumentListItem[] = [];
          let total = 0;
          for (let p = 1; p <= page; p++) {
            const offset = (p - 1) * limit;
            const out = await props.queryClient.fetchQuery({
              queryKey: queryKeys.resource.listPage({
                orgId: props.orgId,
                type: ResourceType.Document,
                limit,
                offset,
                ...(search !== undefined && search !== '' && { search }),
                ...(sort !== undefined && sort !== '' && { sort, sortDir }),
              }),
              staleTime: RESOURCE_LIST_FETCH_STALE_MS,
              queryFn: async () =>
                Assert.assertOk(
                  await props.client.resource.list({
                    type: ResourceType.Document,
                    limit,
                    offset,
                    ...(search !== undefined && search !== '' && { search }),
                    ...(sort !== undefined && sort !== '' && { sort, sortDir }),
                  })
                ),
            });
            if (out.type !== 'document') {
              break;
            }
            total = out.total;
            const docs = out.items.map(
              (doc): DocumentListItem => ({
                id: doc.id,
                ownerId: doc.ownerId,
                title: doc.title,
                description: doc.description,
                folderName: doc.folderName ?? null,
                folderScope: doc.folderScope ?? null,
                scope: doc.scope ?? null,
                // Constructed rather than passed through: the typed client
                // types this as a `Date`, but nothing revives it from the JSON
                // and an ISO string is what arrives. Same defect, same crash in
                // this model's equality function as in the datasets list.
                updatedAt: new Date(doc.updatedAt),
              })
            );
            allItems.push(...docs);
            if (out.items.length < limit) {
              break;
            }
          }
          return { items: allItems, total };
        },
      });
  }

  get listParamsWatchable(): ReadonlyWatchable<ListParamsState> {
    return this._listParams;
  }

  get documentsDataWatchable(): ReadonlyEventWatchable<DocumentsListData | null> {
    return this._documentsDataEventWatchable;
  }

  /** Sync list params from URL and refetch documents data for the current params. */
  syncFromUrl(searchParams: URLSearchParams): void {
    this._listParamsUrl.syncFromUrl(searchParams);
    void this._documentsDataEventWatchable.refresh();
  }

  /** Set list params to the given page and take. Refetch is triggered by syncFromUrl when URL updates. */
  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, page, limit: take });
  }

  /** Set search query; resets page to 1. Refetch is triggered by syncFromUrl when URL updates. */
  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, search, page: 1 });
  }

  /** Set sort key and direction; resets page to 1. Refetch is triggered by syncFromUrl when URL updates. */
  setSort(sort: string | undefined, sortDir: 'asc' | 'desc'): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, sort, sortDir, page: 1 });
  }

  dispose(): void {
    this._documentsDataEventWatchable.unsubscribe();
    this._listParamsUrl.dispose();
  }
}

export const useDocumentsListPageModel = (): DocumentsListPageModel => {
  const model = useInScopeModel(DocumentsListPageModel);
  if (model === null) {
    throw new Error('DocumentsListPageModel not found in scope');
  }
  return model;
};
